// Recorder on the phone (ТЗ §8.1–8.4, §8.9).
// MediaRecorder emits a 10 s chunk every timeslice; each chunk is saved to IndexedDB before anything else.
// Pause or interruption stops the recorder; «Продолжить» opens a new stream (own header) in the same recording.

import { putRecording, putSegment, getRecording, segmentsOf, sha256Hex, type LocalRecording } from "./db.ts";
import { uploader } from "./uploader.ts";

export const SEGMENT_MS = 10_000;
export const AUTOPAUSE_FINISH_MS = 30 * 60_000;

export type RecorderStatus = "idle" | "recording" | "paused" | "autopaused";

export interface RecorderState {
  status: RecorderStatus;
  visitId?: string;
  recordingNo?: number;
  userId?: string;
  patientName?: string;
  visitLabel?: string;
  mimeType?: string;
  elapsedMs: number;
  level: number; // 0..1
  stream: number;
  produced: number;
  autopauseReason?: "hidden" | "interrupted" | "error";
  autopauseAt?: number;
  silenceWarning: boolean;
  lowBattery: boolean;
  limitWarned: boolean;
  warnMinutes: number;
  stopMinutes: number;
  error?: string;
  storageFull?: boolean;
}

const SESSION_KEY = "aisol-med.active-recording";

export function pickMimeType(): { mimeType: string; format: "webm" | "mp4" | "ogg" } | null {
  if (typeof MediaRecorder === "undefined") return null;
  const options: [string, "webm" | "mp4" | "ogg"][] = [
    ["audio/webm;codecs=opus", "webm"],
    ["audio/webm", "webm"],
    ["audio/mp4;codecs=mp4a.40.2", "mp4"],
    ["audio/mp4", "mp4"],
    ["audio/ogg;codecs=opus", "ogg"],
  ];
  for (const [m, f] of options) if (MediaRecorder.isTypeSupported?.(m)) return { mimeType: m, format: f };
  return null;
}

type Listener = () => void;

class Recorder {
  state: RecorderState = this.initial();
  private listeners = new Set<Listener>();
  private media?: MediaStream;
  private rec?: MediaRecorder;
  private ctx?: AudioContext;
  private analyser?: AnalyserNode;
  private raf?: number;
  private tick?: number;
  private wakeLock?: WakeLockSentinel | null;
  private streamStartedAt = 0;
  private lastChunkAt = 0;
  private accumulatedMs = 0;
  private seq = 0;
  private silentSince = 0;
  private heardSound = false;
  private finishing?: Promise<void>;
  private chain: Promise<void> = Promise.resolve();
  private stopResolve?: () => void;

  private initial(): RecorderState {
    return { status: "idle", elapsedMs: 0, level: 0, stream: 0, produced: 0, silenceWarning: false, lowBattery: false, limitWarned: false, warnMinutes: 45, stopMinutes: 60 };
  }

  subscribe = (l: Listener) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  getState = () => this.state;
  private set(p: Partial<RecorderState>) {
    this.state = { ...this.state, ...p };
    this.listeners.forEach((l) => l());
    this.persist();
  }

  private persist() {
    try {
      const s = this.state;
      if (s.status === "idle") localStorage.removeItem(SESSION_KEY);
      else
        localStorage.setItem(
          SESSION_KEY,
          JSON.stringify({ visitId: s.visitId, recordingNo: s.recordingNo, userId: s.userId, patientName: s.patientName, visitLabel: s.visitLabel, mimeType: s.mimeType, elapsedMs: s.elapsedMs, stream: s.stream, produced: s.produced, autopauseAt: s.autopauseAt ?? Date.now(), warnMinutes: s.warnMinutes, stopMinutes: s.stopMinutes }),
        );
    } catch {
      /* ignore */
    }
  }

  /** After a reload or a closed tab: the interrupted recording comes back as «autopaused». */
  async restore(userId: string): Promise<void> {
    if (this.state.status !== "idle") return;
    let saved: (Partial<RecorderState> & { autopauseAt?: number }) | null = null;
    try {
      saved = JSON.parse(localStorage.getItem(SESSION_KEY) ?? "null");
    } catch {
      saved = null;
    }
    if (!saved?.visitId || saved.userId !== userId) return;
    const local = await getRecording(userId, saved.visitId, saved.recordingNo!);
    if (!local || local.finished) {
      localStorage.removeItem(SESSION_KEY);
      return;
    }
    const segs = await segmentsOf(userId, saved.visitId, saved.recordingNo!);
    this.seq = Math.max(local.produced, segs.length ? segs[segs.length - 1].seq + 1 : 0);
    this.accumulatedMs = local.durationMs;
    this.set({
      ...this.initial(),
      status: "autopaused",
      autopauseReason: "interrupted",
      autopauseAt: saved.autopauseAt ?? Date.now(),
      visitId: saved.visitId,
      recordingNo: saved.recordingNo,
      userId,
      patientName: saved.patientName,
      visitLabel: saved.visitLabel,
      mimeType: local.mimeType,
      elapsedMs: local.durationMs,
      stream: (saved.stream ?? 0) + 1,
      produced: this.seq,
      warnMinutes: saved.warnMinutes ?? 45,
      stopMinutes: saved.stopMinutes ?? 60,
    });
    this.checkAutopauseTimeout();
  }

  async start(opts: { userId: string; visitId: string; recordingNo: number; mimeType: string; format: "webm" | "mp4" | "ogg"; patientName?: string; visitLabel?: string; warnMinutes: number; stopMinutes: number }): Promise<void> {
    const local: LocalRecording = {
      userId: opts.userId,
      visitId: opts.visitId,
      recordingNo: opts.recordingNo,
      mimeType: opts.mimeType,
      format: opts.format,
      patientName: opts.patientName,
      startedAt: new Date().toISOString(),
      produced: 0,
      durationMs: 0,
    };
    const existing = await getRecording(opts.userId, opts.visitId, opts.recordingNo);
    if (!existing) await putRecording(local);
    this.seq = existing?.produced ?? 0;
    this.accumulatedMs = existing?.durationMs ?? 0;
    this.set({
      ...this.initial(),
      status: "recording",
      visitId: opts.visitId,
      recordingNo: opts.recordingNo,
      userId: opts.userId,
      patientName: opts.patientName,
      visitLabel: opts.visitLabel,
      mimeType: opts.mimeType,
      elapsedMs: this.accumulatedMs,
      stream: existing ? 1 : 0,
      produced: this.seq,
      warnMinutes: opts.warnMinutes,
      stopMinutes: opts.stopMinutes,
    });
    try {
      await this.openStream();
    } catch (e) {
      this.set({ status: "autopaused", autopauseReason: "error", autopauseAt: Date.now(), error: micError(e) });
      throw e;
    }
    this.bindPageEvents();
  }

  private async openStream(): Promise<void> {
    this.media = await navigator.mediaDevices.getUserMedia({ audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true } });
    const track = this.media.getAudioTracks()[0];
    track.addEventListener("ended", () => this.autopause("interrupted"));
    track.addEventListener("mute", () => {
      // iOS mutes the track during a phone call
      if (this.state.status === "recording") this.autopause("interrupted");
    });
    let rec: MediaRecorder;
    try {
      rec = new MediaRecorder(this.media, { mimeType: this.state.mimeType, audioBitsPerSecond: 40_000 });
    } catch {
      rec = new MediaRecorder(this.media);
    }
    const stream = this.state.stream;
    this.streamStartedAt = Date.now();
    this.lastChunkAt = this.streamStartedAt;
    rec.ondataavailable = (ev) => {
      if (!ev.data || ev.data.size === 0) return;
      const now = Date.now();
      const durationMs = Math.max(1, now - this.lastChunkAt);
      this.lastChunkAt = now;
      const seq = this.seq++;
      this.chain = this.chain.then(() => this.saveChunk(ev.data, seq, stream, durationMs));
    };
    rec.onstop = () => {
      this.chain.then(() => this.stopResolve?.());
    };
    rec.onerror = () => this.autopause("error");
    rec.start(SEGMENT_MS);
    this.rec = rec;
    this.startMeter();
    this.startTick();
    await this.requestWakeLock();
    this.silentSince = Date.now();
    this.heardSound = false;
  }

  private async saveChunk(blob: Blob, seq: number, stream: number, durationMs: number): Promise<void> {
    const s = this.state;
    if (!s.userId || !s.visitId || s.recordingNo === undefined) return;
    const sha256 = await sha256Hex(blob);
    try {
      await putSegment({ userId: s.userId, visitId: s.visitId, recordingNo: s.recordingNo, seq, stream, blob, sha256, durationMs, recordedAt: new Date().toISOString() });
      this.set({ storageFull: false });
    } catch (e) {
      // storage full: keep recording, send directly while online (ТЗ §8.5)
      console.warn("segment not stored locally", e);
      this.set({ storageFull: true });
      uploader.sendDirect({ userId: s.userId, visitId: s.visitId, recordingNo: s.recordingNo, seq, stream, blob, sha256, durationMs, recordedAt: new Date().toISOString() });
    }
    this.accumulatedMs += durationMs;
    const local = await getRecording(s.userId, s.visitId, s.recordingNo);
    if (local) await putRecording({ ...local, produced: Math.max(local.produced, seq + 1), durationMs: this.accumulatedMs });
    this.set({ produced: Math.max(this.state.produced, seq + 1) });
    uploader.kick();
  }

  private stopStream(): Promise<void> {
    cancelAnimationFrame(this.raf ?? 0);
    clearInterval(this.tick);
    const rec = this.rec;
    this.rec = undefined;
    const done = new Promise<void>((resolve) => {
      this.stopResolve = resolve;
      setTimeout(resolve, 4000);
    });
    try {
      if (rec && rec.state !== "inactive") rec.stop();
      else this.stopResolve?.();
    } catch {
      this.stopResolve?.();
    }
    this.media?.getTracks().forEach((t) => t.stop());
    this.media = undefined;
    this.ctx?.close().catch(() => {});
    this.ctx = undefined;
    this.releaseWakeLock();
    return done.then(() => this.chain);
  }

  async pause(): Promise<void> {
    if (this.state.status !== "recording") return;
    await this.stopStream();
    this.set({ status: "paused", level: 0, elapsedMs: this.accumulatedMs, stream: this.state.stream + 1 });
    uploader.event(this.state.visitId!, this.state.recordingNo!, "pause");
  }

  async resume(): Promise<void> {
    if (this.state.status !== "paused" && this.state.status !== "autopaused") return;
    const wasAuto = this.state.status === "autopaused";
    this.set({ status: "recording", autopauseReason: undefined, autopauseAt: undefined, error: undefined });
    try {
      await this.openStream();
      this.bindPageEvents();
      uploader.event(this.state.visitId!, this.state.recordingNo!, "resume", wasAuto ? "после автопаузы" : undefined);
    } catch (e) {
      this.set({ status: "autopaused", autopauseReason: "error", autopauseAt: Date.now(), error: micError(e) });
      throw e;
    }
  }

  private autopause(reason: "hidden" | "interrupted" | "error") {
    if (this.state.status !== "recording") return;
    void this.stopStream().then(() => {
      this.set({ status: "autopaused", autopauseReason: reason, autopauseAt: Date.now(), level: 0, elapsedMs: this.accumulatedMs, stream: this.state.stream + 1 });
      uploader.event(this.state.visitId!, this.state.recordingNo!, "autopause", reason === "hidden" ? "браузер свёрнут или экран заблокирован" : reason === "interrupted" ? "звонок или микрофон занят" : "ошибка записи");
    });
  }

  /** «Завершить запись». Works without network: the uploader sends the rest later. */
  async finish(endedBy: "doctor" | "autopause" | "limit" = "doctor"): Promise<{ visitId: string; recordingNo: number; durationMs: number; segments: number } | null> {
    if (this.finishing) {
      await this.finishing;
      return null;
    }
    const s = this.state;
    if (s.status === "idle" || !s.visitId) return null;
    let result: { visitId: string; recordingNo: number; durationMs: number; segments: number } | null = null;
    this.finishing = (async () => {
      if (this.rec) await this.stopStream();
      await this.chain;
      const local = await getRecording(s.userId!, s.visitId!, s.recordingNo!);
      const segmentsTotal = Math.max(local?.produced ?? 0, this.seq);
      const durationMs = this.accumulatedMs;
      if (local) await putRecording({ ...local, produced: segmentsTotal, durationMs, finished: { endedAt: new Date().toISOString(), endedBy, segmentsTotal, durationMs } });
      result = { visitId: s.visitId!, recordingNo: s.recordingNo!, durationMs, segments: segmentsTotal };
      this.unbindPageEvents();
      this.set({ ...this.initial() });
      uploader.kick();
    })();
    try {
      await this.finishing;
    } finally {
      this.finishing = undefined;
    }
    return result;
  }

  // --- meter, timer, limits, silence ---
  private startMeter() {
    try {
      const Ctx = window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      this.ctx = new Ctx();
      const src = this.ctx.createMediaStreamSource(this.media!);
      this.analyser = this.ctx.createAnalyser();
      this.analyser.fftSize = 512;
      src.connect(this.analyser);
      const buf = new Uint8Array(this.analyser.fftSize);
      const loop = () => {
        if (!this.analyser) return;
        this.analyser.getByteTimeDomainData(buf);
        let sum = 0;
        for (const v of buf) sum += ((v - 128) / 128) ** 2;
        const rms = Math.sqrt(sum / buf.length);
        const level = Math.min(1, rms * 4);
        if (rms > 0.02) this.heardSound = true;
        if (Math.abs(level - this.state.level) > 0.02) this.set({ level });
        this.raf = requestAnimationFrame(loop);
      };
      loop();
    } catch {
      /* the meter is optional */
    }
  }

  private startTick() {
    clearInterval(this.tick);
    this.tick = window.setInterval(() => {
      const s = this.state;
      if (s.status !== "recording") return;
      const elapsed = this.accumulatedMs + (Date.now() - this.lastChunkAt);
      const patch: Partial<RecorderState> = { elapsedMs: elapsed };
      if (!this.heardSound && Date.now() - this.silentSince > 10_000 && !s.silenceWarning) {
        patch.silenceWarning = true;
        uploader.event(s.visitId!, s.recordingNo!, "silence");
      }
      if (this.heardSound && s.silenceWarning) patch.silenceWarning = false;
      if (!s.limitWarned && elapsed > s.warnMinutes * 60_000) patch.limitWarned = true;
      this.set(patch);
      if (elapsed > s.stopMinutes * 60_000) void this.finish("limit");
      if (Math.floor(elapsed / 1000) % 60 === 0) void this.checkBattery();
    }, 500);
  }

  private async checkBattery() {
    try {
      const b = await (navigator as Navigator & { getBattery?: () => Promise<{ level: number; charging: boolean }> }).getBattery?.();
      if (b && !b.charging && b.level < 0.1 && !this.state.lowBattery) this.set({ lowBattery: true });
    } catch {
      /* not supported (Safari) */
    }
  }

  checkAutopauseTimeout() {
    const s = this.state;
    if (s.status === "autopaused" && s.autopauseAt && Date.now() - s.autopauseAt > AUTOPAUSE_FINISH_MS) void this.finish("autopause");
  }

  // --- screen wake lock & page lifecycle ---
  private async requestWakeLock() {
    try {
      this.wakeLock = await (navigator as Navigator & { wakeLock?: { request(t: "screen"): Promise<WakeLockSentinel> } }).wakeLock?.request("screen");
    } catch {
      this.wakeLock = null;
    }
  }
  private releaseWakeLock() {
    this.wakeLock?.release().catch(() => {});
    this.wakeLock = null;
  }

  private onVisibility = () => {
    if (document.visibilityState === "hidden") this.autopause("hidden");
    else {
      if (this.state.status === "recording") void this.requestWakeLock();
      this.checkAutopauseTimeout();
    }
  };
  private onPageHide = () => this.autopause("hidden");
  private bound = false;
  private bindPageEvents() {
    if (this.bound) return;
    this.bound = true;
    document.addEventListener("visibilitychange", this.onVisibility);
    window.addEventListener("pagehide", this.onPageHide);
  }
  private unbindPageEvents() {
    this.bound = false;
    document.removeEventListener("visibilitychange", this.onVisibility);
    window.removeEventListener("pagehide", this.onPageHide);
  }
}

function micError(e: unknown): string {
  const name = (e as { name?: string })?.name;
  if (name === "NotAllowedError" || name === "SecurityError") return "MIC_DENIED";
  if (name === "NotFoundError") return "MIC_MISSING";
  if (name === "NotReadableError") return "MIC_BUSY";
  return "MIC_ERROR";
}

export const recorder = new Recorder();

setInterval(() => recorder.checkAutopauseTimeout(), 30_000);
